import { Injectable, ApplicationRef, signal, inject } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { first, concatMap, throttleTime, filter } from 'rxjs/operators';
import { interval, BehaviorSubject, fromEvent, merge, asyncScheduler, type Observable } from 'rxjs';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root'
})
export class UpdateService {
  private toastService = inject(ToastService);

  // BehaviorSubject to hold the status of the initial check. Starts as false.
  private initialCheckDone = new BehaviorSubject<boolean>(false);
  
  // Public observable that components can subscribe to.
  public initialCheckDone$: Observable<boolean> = this.initialCheckDone.asObservable();

  // Signal to track whether a version update is downloading
  public readonly isDownloading = signal<boolean>(false);

  // Signal to track if an update check is currently active
  public readonly isChecking = signal<boolean>(false);

  // Set of version hashes that are currently downloading to prevent duplicate logging/state changes
  private downloadingHashes = new Set<string>();

  // Cooldown tracker for update checks to prevent spamming
  private lastCheckTime = 0;
  private readonly CHECK_COOLDOWN_MS = 60 * 1000; // 1 minute cooldown

  constructor(appRef: ApplicationRef, private swUpdate: SwUpdate) {
    if (!this.swUpdate.isEnabled) {
      // If the service worker is disabled, we can't check for updates.
      // We'll immediately set the check to "done" so the app doesn't hang.
      this.initialCheckDone.next(true);
      return;
    }

    // Subscribe to version updates to show the reload prompt.
    this.handleUpdateActivation();

    // Set up background polling and event-driven update checking after the app is stable.
    const appIsStable$ = appRef.isStable.pipe(first(isStable => isStable === true));
    
    // Check every 5 minutes
    const everyFiveMinutes$ = interval(5 * 60 * 1000);
    
    // Check when window gains focus
    const windowFocus$ = fromEvent(window, 'focus');
    
    // Check when visibility state changes to visible
    const visibilityChange$ = fromEvent(document, 'visibilitychange').pipe(
      filter(() => document.visibilityState === 'visible')
    );

    // Merge triggers
    const updateTriggers$ = merge(
      everyFiveMinutes$,
      windowFocus$,
      visibilityChange$
    ).pipe(
      // Cooldown to avoid duplicate checks in quick succession
      throttleTime(this.CHECK_COOLDOWN_MS, asyncScheduler, { leading: true, trailing: false })
    );

    appIsStable$.pipe(concatMap(() => updateTriggers$)).subscribe(() => {
      console.log('Triggering background check for updates...');
      this.checkForUpdateSafe();
    });
  }

  /**
   * Helper to perform a safe check with cooldown and state tracking.
   */
  public async checkForUpdateSafe(): Promise<boolean> {
    if (!this.swUpdate.isEnabled) {
      return false;
    }

    const now = Date.now();
    if (now - this.lastCheckTime < this.CHECK_COOLDOWN_MS) {
      console.log('Skipping update check (cooldown active)');
      return false;
    }
    this.lastCheckTime = now;

    if (this.isChecking() || this.isDownloading()) {
      console.log('Skipping update check (already checking or downloading)');
      return false;
    }

    try {
      this.isChecking.set(true);
      console.log('Checking for updates...');
      const updateFound = await this.swUpdate.checkForUpdate();
      console.log(updateFound ? 'An update is available and starting to download.' : 'No update available.');
      return updateFound;
    } catch (error) {
      console.error('Update check failed:', error);
      return false;
    } finally {
      this.isChecking.set(false);
    }
  }

  /**
   * This method is called by AppComponent to trigger the first update check.
   */
  public async performInitialUpdateCheck(): Promise<void> {
    if (!this.swUpdate.isEnabled) {
      return;
    }
    
    try {
      console.log('Performing initial update check...');
      this.isChecking.set(true);
      this.lastCheckTime = Date.now();
      const updateFound = await this.swUpdate.checkForUpdate();
      console.log(updateFound ? 'Initial check found a new version.' : 'Initial check confirmed latest version.');
    } catch (error) {
      console.error('Initial update check failed:', error);
    } finally {
      this.isChecking.set(false);
      // IMPORTANT: Whether the check succeeded or failed, we now mark it as "done".
      // This ensures the application can always proceed, even if the user is offline.
      console.log('Initial check is complete. Application can now proceed.');
      this.initialCheckDone.next(true);
    }
  }

  private handleUpdateActivation(): void {
    this.swUpdate.versionUpdates.subscribe(evt => {
      switch (evt.type) {
        case 'VERSION_DETECTED':
          const hash = evt.version.hash;
          if (this.downloadingHashes.has(hash)) {
            console.log(`Already downloading/downloaded version: ${hash}`);
            break;
          }
          this.downloadingHashes.add(hash);
          console.log(`Downloading new app version: ${hash}`);
          this.isDownloading.set(true);
          break;

        case 'VERSION_READY':
          console.log(`Current app version: ${evt.currentVersion.hash}`);
          console.log(`New app version ready for use: ${evt.latestVersion.hash}`);
          this.isDownloading.set(false);
          
          console.log('Activating update and reloading page...');
          this.swUpdate.activateUpdate().then(() => {
            document.location.reload();
          }).catch(err => {
            console.error('Error activating update, reloading anyway:', err);
            document.location.reload();
          });
          break;

        case 'VERSION_INSTALLATION_FAILED':
          console.error(`Failed to install app version '${evt.version.hash}': ${evt.error}`);
          this.isDownloading.set(false);
          this.toastService.add({
            severity: 'warn',
            summary: 'Update Failed',
            detail: 'A new version failed to install. You can continue using NorthAI, and we will try again later.',
            life: 6000
          });
          break;

        case 'NO_NEW_VERSION_DETECTED':
          console.log('No new version detected');
          this.isDownloading.set(false);
          break;
      }
    });
  }
}

